// src/components/SubscriptionManagement/AddOnsSection.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Switch,
  Button,
  Paper,
  Chip,
  Skeleton,
  Tooltip,
  Alert,
} from "@mui/material";
import { ColorsConstants, grayFontColor } from "../../cssUtils/constants.js";
import { Info } from "@mui/icons-material";
import typography from "../../cssUtils/typography.js";
import BackButton from "../../common/back-button.jsx";
import ButtonOne from "../../common/normal-button.jsx";
import {
  getCurrencySymbol,
  getPlanPricing,
  getPlanPrice,
  BILLING_CYCLE_CONFIG,
  normalizeCycle,
} from "./utils.js";
import AddonQuantityModal from "./AddonQuantityModal.js";

// Maps a substring of the addon slug → { unitLabel (singular) }
// More-specific patterns must come before generic ones.
const CONFIGURABLE_ADDON_CONFIG = {
  additional_agents: { unitLabel: "agent" },
  rules_based_chatbot: { unitLabel: "chatbot" },
  ai_based_chatbot: { unitLabel: "chatbot" },
  chatbot: { unitLabel: "chatbot" },
  custom_reports: { unitLabel: "report" },
  additional_whatsapp_number: { unitLabel: "number" },
  custom_integrations: { unitLabel: "integration" },
};

const getAddonConfig = (addonName) => {
  if (!addonName) return null;
  const lower = addonName.toLowerCase();
  for (const [pattern, cfg] of Object.entries(CONFIGURABLE_ADDON_CONFIG)) {
    if (lower.includes(pattern)) return cfg;
  }
  return null;
};

const needsConfiguration = (addonName) => !!getAddonConfig(addonName);

const AddOnCard = ({
  addon,
  selectedBillingCycle,
  currentAddons,
  selectedAddons,
  billingCurrency,
  onAddonToggle,
  addonPurchaseMode,
}) => {
  const [configModalOpen, setConfigModalOpen] = useState(false);

  const isOneTime = addon.addon_type === 'one_time';
  const effectiveCycle = isOneTime ? 'one_time' : selectedBillingCycle;

  const isAddonSelected = (addonId) =>
    selectedAddons.some((a) => a.id === addonId);
  const isCurrentAddon = (addon) =>
    currentAddons.some((a) => a.id === addon.id);

  const getAllPricingOptions = (addon) => {
    if (!addon?.pricing) return [];
    if (isOneTime) return []; // one-time addons only have one pricing option
    return addon.pricing.filter(
      (a) =>
        a.billing_cycle === "monthly" ||
        a.billing_cycle === "quarterly" ||
        a.billing_cycle === "yearly",
    );
  };

  const getAddonDisplayName = (addonName) => {
    if (!addonName) return "";
    return addonName
      .split("_")
      .map((word) => {
        if (word.toLowerCase() === 'rcs') return 'RCS';
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  };

  const formatPrice = (price) => {
    if (price === 0 || price === "0") return "Contact Support";
    return `${getCurrencySymbol(billingCurrency)}${parseFloat(price).toLocaleString("en-IN")}`;
  };

  const isSelected = isAddonSelected(addon.id);
  const isCurrent = isCurrentAddon(addon);
  const basePrice = getPlanPrice(addon, effectiveCycle);
  const pricingOptions = getAllPricingOptions(addon);

  const activePricingEntry = getPlanPricing(addon, effectiveCycle);
  const isAvailable = !!(
    activePricingEntry && parseFloat(activePricingEntry.price) > 0
  );
  const availability = addon.is_active
    ? "Available for your plan"
    : "Currently Unavailable";

  // When selected, use the price stored on the selected addon (reflects quantity)
  const selectedAddonEntry = selectedAddons.find((a) => a.id === addon.id);
  const displayPrice =
    isSelected && selectedAddonEntry?.price != null
      ? selectedAddonEntry.price
      : basePrice;
  const selectedQuantity = selectedAddonEntry?.quantity;

  // Unit label for configurable addons (e.g. "agent", "chatbot", "report")
  const unitLabel = getAddonConfig(addon.name)?.unitLabel ?? "unit";

  // Current quantity already active on the subscription (0 if not current)
  const currentAddonEntry = currentAddons.find((a) => a.id === addon.id);
  const currentQuantity = currentAddonEntry?.currentQuantity ?? 1;

  // One-time addons are never quantity-configurable
  const needsConfigForAddon = !isOneTime && needsConfiguration(addon.name);

  // In addon-purchase mode, configurable addons that are already active can have
  // their quantity increased — the switch becomes interactive.
  const canIncreaseQty =
    addonPurchaseMode && isCurrent && needsConfigForAddon;

  // Handle addon toggle
  const handleAddonToggle = (addon, isChecked) => {
    if (!isAvailable) return;

    if (isChecked && needsConfigForAddon) {
      setConfigModalOpen(true);
      return;
    }

    const addonWithData = {
      ...addon,
      displayName: getAddonDisplayName(addon.name),
      price: basePrice,
      billing_cycle: isOneTime ? 'one_time' : selectedBillingCycle,
    };
    onAddonToggle(addonWithData, isChecked);
  };

  const handleConfigConfirm = (newTotalQty) => {
    const addonWithData = {
      ...addon,
      displayName: getAddonDisplayName(addon.name),
      price: basePrice * newTotalQty,
      pricePerUnit: basePrice,
      quantity: newTotalQty,
      billing_cycle: selectedBillingCycle,
    };
    onAddonToggle(addonWithData, true);
    setConfigModalOpen(false);
  };

  return (
    <Grid item xs={12} sm={6} md={4} key={addon.id}>
      <Card
        sx={{
          height: "100%",
          border: isSelected
            ? `2px solid ${ColorsConstants.primaryPurple100}`
            : `1px solid ${ColorsConstants.primaryGray10}`,
          transition: "all 0.3s",
          backgroundColor: isSelected ? ColorsConstants.primaryBlue60 : "white",
          opacity: isAvailable ? 1 : 0.6,
          "&:hover": {
            borderColor: isAvailable
              ? ColorsConstants.primaryPurple100
              : ColorsConstants.primaryGray10,
            boxShadow: isAvailable ? 4 : 0,
          },
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography
                  variant="h6"
                  sx={{
                    ...typography.h1,
                    color: ColorsConstants.primaryBackgroundColor,
                    mb: 0.5,
                  }}
                >
                  {getAddonDisplayName(addon.name)}
                </Typography>
                <Chip
                  label={isOneTime ? "One-Time" : "Recurring"}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: "0.65rem",
                    borderColor: isOneTime ? "#D97706" : "#6366F1",
                    color: isOneTime ? "#D97706" : "#6366F1",
                  }}
                />
                {isCurrent && (
                  <Chip
                    label={canIncreaseQty ? `${currentQuantity} Active` : "Active"}
                    size="small"
                    color={canIncreaseQty ? "info" : "success"}
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  ...typography.bodySmall,
                  color: isAvailable
                    ? grayFontColor
                    : ColorsConstants.primaryGray40,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {availability}
                {!isAvailable && (
                  <Tooltip title="Upgrade to Advanced plan to access this add-on">
                    <Info sx={{ fontSize: 14 }} />
                  </Tooltip>
                )}
              </Typography>
            </Box>
            <Switch
              checked={isSelected || isCurrent}
              onChange={(e) => handleAddonToggle(addon, e.target.checked)}
              disabled={!isAvailable || isCurrent}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#48bb78",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#48bb78",
                },
                "& .Mui-disabled": {
                  color: ColorsConstants.primaryGray30,
                },
              }}
            />
          </Box>

          <Typography
            variant="h5"
            sx={{
              ...typography.h1,
              fontWeight: "bold",
              color: ColorsConstants.primaryBackgroundColor,
              my: 2,
            }}
          >
            {!isAvailable ? "Contact Support" : formatPrice(displayPrice)}

            {isAvailable && displayPrice > 0 && (
              <Typography
                component="span"
                variant="h6"
                sx={{ color: ColorsConstants.primaryGray40 }}
              >
                {isOneTime
                  ? " (one-time)"
                  : (BILLING_CYCLE_CONFIG[normalizeCycle(selectedBillingCycle)]
                      ?.suffix ?? "")}
              </Typography>
            )}
          </Typography>

          {/* Show quantity breakdown when a configurable addon is selected */}
          {isSelected && selectedQuantity && (
            <Typography
              variant="caption"
              sx={{ color: grayFontColor, display: "block", mt: -1.5, mb: 1 }}
            >
              {canIncreaseQty
                ? `${currentQuantity} → ${selectedQuantity} ${unitLabel}${selectedQuantity !== 1 ? "s" : ""} (+${selectedQuantity - currentQuantity})`
                : `${selectedQuantity} ${unitLabel}${selectedQuantity !== 1 ? "s" : ""} × ${formatPrice(basePrice)}`}
            </Typography>
          )}

          {/* Show other pricing options only if the addon is active */}
          {isAvailable && pricingOptions && pricingOptions.length > 1 && (
            <Box sx={{ mb: 2 }}>
              {pricingOptions
                .filter(
                  (p) =>
                    normalizeCycle(p.billing_cycle) !==
                    normalizeCycle(selectedBillingCycle),
                )
                .map((pricing) => (
                  <Typography
                    key={pricing.billing_cycle}
                    variant="caption"
                    sx={{
                      ...typography.bodySmall,
                      color: ColorsConstants.primaryGray50,
                      display: "block",
                      mt: 0.5,
                    }}
                  >
                    {formatPrice(pricing.price)}/{pricing.billing_cycle}
                  </Typography>
                ))}
            </Box>
          )}

          <Typography
            variant="body2"
            sx={{
              ...typography.bodyLarge,
              color: grayFontColor,
              minHeight: 60,
            }}
          >
            {addon.description}
          </Typography>

          {isCurrent && (
            <Typography
              variant="caption"
              sx={{
                color: "#48bb78",
                fontWeight: "bold",
                display: "block",
                mt: 1,
              }}
            >
              ✓ {isOneTime ? "Already activated" : "Already part of your subscription"}
            </Typography>
          )}

          {canIncreaseQty && (
            <ButtonOne
              variant="outlined"
              onClick={() => setConfigModalOpen(true)}
              sx={{ mt: 1.5, width: "100%" }}
            >
              {isSelected
                ? `Update Quantity (→ ${selectedQuantity})`
                : "+ Add More"}
            </ButtonOne>
          )}
        </CardContent>
      </Card>

      <AddonQuantityModal
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        onConfirm={handleConfigConfirm}
        addon={addon}
        selectedBillingCycle={selectedBillingCycle}
        billingCurrency={billingCurrency}
        unitLabel={unitLabel}
        currentQuantity={canIncreaseQty ? currentQuantity : 0}
      />
    </Grid>
  );
};

const AddOnsSection = ({
  addons = [],
  currentAddons = [],
  selectedAddons = [],
  selectedPlan,
  selectedBillingCycle = "quarterly",
  billingCurrency = "INR",
  onAddonToggle,
  onGoToPlans,
  onContinueToCheckout,
  loading = false,
  setCheckoutPage,
  checkoutPage,
  addonPurchaseMode = false,
}) => {
  if (loading) {
    return (
      <Box>
        <Card sx={{ mb: 4, p: 3 }}>
          <Skeleton
            variant="text"
            width="60%"
            height={40}
            sx={{ mx: "auto" }}
          />
          <Skeleton
            variant="text"
            width="40%"
            height={30}
            sx={{ mx: "auto", mt: 1 }}
          />
        </Card>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Skeleton variant="text" width="70%" height={30} />
                  <Skeleton variant="text" width="50%" height={20} />
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={40}
                    sx={{ my: 2 }}
                  />
                  <Skeleton variant="text" width="100%" height={60} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!selectedPlan) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Paper
          sx={{
            p: 6,
            textAlign: "center",
            maxWidth: 400,
            backgroundColor: "white",
            border: `2px solid ${ColorsConstants.primaryGray10}`,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: ColorsConstants.primaryBackgroundColor,
              mb: 2,
            }}
          >
            🔒 Select a Plan First
          </Typography>
          <Typography variant="body1" sx={{ color: grayFontColor, mb: 3 }}>
            Please select a subscription plan before choosing add-ons
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onGoToPlans}
            sx={{
              backgroundColor: ColorsConstants.primaryPurple100,
              fontWeight: "bold",
              px: 4,
            }}
          >
            Go to Plans
          </Button>
        </Paper>
      </Box>
    );
  }

  if (addons.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No add-ons available at the moment.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {onGoToPlans && (
        <Box sx={{ mb: 2 }}>
          <BackButton onClick={onGoToPlans} />
        </Box>
      )}

      {addonPurchaseMode && (
        <Alert
          severity="info"
          icon={<Info />}
          sx={{
            mb: 3,
            borderRadius: 2,
            backgroundColor: "#EFF6FF",
            color: "#1E40AF",
            "& .MuiAlert-icon": { color: "#1E40AF" },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Mid-Cycle Add-on Purchase
          </Typography>
          <Typography variant="body2">
            <strong>Recurring</strong> add-ons are prorated — you'll only be
            charged for the remaining days in your current billing period.{" "}
            <strong>One-Time</strong> add-ons are charged at full price once and
            do not renew. Your existing subscription remains unchanged.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 🟢 FIX 1: Removed the .filter(addon => addon.is_active) so all addons render */}
        {addons.map((addon) => (
          <AddOnCard
            key={addon.id}
            addon={addon}
            selectedBillingCycle={selectedBillingCycle}
            currentAddons={currentAddons}
            selectedAddons={selectedAddons}
            billingCurrency={billingCurrency}
            onAddonToggle={onAddonToggle}
            addonPurchaseMode={addonPurchaseMode}
          />
        ))}
      </Grid>

      {/* Continue to Checkout Button */}
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            onContinueToCheckout();
            setCheckoutPage(true);
          }}
          sx={{
            backgroundColor: ColorsConstants.primaryPurple100,
            color: "white",
            fontWeight: "bold",
            px: 6,
            py: 1.5,
            fontSize: "1.1rem",
            "&:hover": {
              backgroundColor: ColorsConstants.primaryBackgroundColor,
            },
          }}
        >
          Continue to Checkout →
        </Button>
        <Typography
          variant="caption"
          sx={{
            color: grayFontColor,
            display: "block",
            mt: 1,
          }}
        >
          Add-ons are optional. You can proceed without selecting any.
        </Typography>
      </Box>
    </Box>
  );
};

export default AddOnsSection;
